//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import React from 'react';
import styled from 'styled-components';

import { PrimaryButton } from '@components/atoms/button/Button';
import { BodyText, TitleText } from '@components/atoms/text/Text';
import OwnedImage from '@images/owned_rights.svg';
import ApprovedImage from '@images/preapproved_rights.svg';
import { RightsCreationSource } from '@models/RightsCreationSource';
import { isDefined } from '@utils/filterPredicates';

import { RIGHTS_STATUS } from 'app/bulk-actions/bulk-actions-constants/bulk-actions-constants';

const rightsStatusConfigs = [
  {
    description:
      "No need to request rights.\nYou're free to add some products,\nand distribute this everywhere!",
    icon: OwnedImage,
    source: RightsCreationSource.ApprovedRightsRequest,
    status: RIGHTS_STATUS.BRAND_OWNED,
    title: 'Rights Owned',
  },
  {
    description:
      "This media belongs to a brand partner that you're collaborating with. You're free to add some products and distribute this everywhere!",
    icon: ApprovedImage,
    source: RightsCreationSource.RelationshipClientToClient,
    status: RIGHTS_STATUS.APPROVED,
    title: 'Rights Approved',
  },
  {
    description:
      "This media belongs to an creators that you've configured auto-rights approval on. You're free to add some products and distribute this everywhere!",
    icon: ApprovedImage,
    source: RightsCreationSource.RelationshipInfluencerToClient,
    status: RIGHTS_STATUS.APPROVED,
    title: 'Rights Approved',
  },
  {
    description:
      "The rights request has been approved. You're free to add some products and distribute this everywhere!",
    icon: ApprovedImage,
    source: RightsCreationSource.ApprovedRightsRequest,
    status: RIGHTS_STATUS.APPROVED,
    title: 'Rights Approved',
  },
  {
    description:
      "This media is imported from Bazaarvoice with rights approved. You're free to add some products and distribute this everywhere!",
    icon: ApprovedImage,
    isBVImport: true,
    source: RightsCreationSource.SkippedRightsRequest,
    status: RIGHTS_STATUS.APPROVED,
    title: 'Rights Approved',
  },
  {
    description:
      "The rights request has been skipped for this media. You're free to add some products and distribute this everywhere!",
    icon: ApprovedImage,
    source: RightsCreationSource.SkippedRightsRequest,
    status: RIGHTS_STATUS.APPROVED,
    title: 'Rights Skipped',
  },
];

export interface UIARequestStatusProps {
  isBVImport?: boolean;
  onCloseModal: () => void;
  source?: RightsCreationSource;
  status: RIGHTS_STATUS;
}

export const UIARequestStatus = ({
  isBVImport,
  onCloseModal,
  source,
  status,
}: UIARequestStatusProps) => {
  const config = rightsStatusConfigs.find((config) => {
    const conditions = [config.status === status, config.source === source];

    if (isBVImport) {
      conditions.push(config.isBVImport === isBVImport);
    }

    return conditions.every((condition) => condition);
  });

  return isDefined(config) ? (
    <Wrapper>
      <Status>
        <Image src={config.icon} alt={config.status} />
        <TitleText>{config.title}</TitleText>
        <BodyText>{config.description}</BodyText>
      </Status>
      <Actions>
        <PrimaryButton onClick={onCloseModal}>Done</PrimaryButton>
      </Actions>
    </Wrapper>
  ) : (
    <></>
  );
};

const Wrapper = styled.div(
  () => `
    display: flex;
    flex-direction: column;
    align-items: center;
    height: 100%;
  `,
);

const Status = styled.div(
  ({ theme }) => `
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: ${theme.spacings[3]};
    padding: ${theme.spacings[6]};
  `,
);

const Image = styled.img(
  ({ theme }) => `
    height: auto;
    width: calc(3 * ${theme.spacings[6]});
  `,
);

const Actions = styled.div(
  ({ theme }) => `
    display: flex;
    justify-content: flex-end;
    align-items: flex-end;
    padding: ${theme.spacings[3]};
    border-top: ${theme.borderWidths[1]} solid ${theme.colors.gray4};
    width: 100%;
  `,
);
